import { DeliveryEligibility, CountdownData } from '../types'

/**
 * Helper function to check if two dates are the same calendar day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return date1.toDateString() === date2.toDateString()
}

/**
 * Get the delivery day name based on eligibility
 *
 * @param eligibility - The delivery eligibility type
 * @param fromDate - Optional date to calculate from (defaults to today)
 * @returns The day name (e.g., "Tuesday", "Wednesday")
 */
export function getDeliveryDayName(
  eligibility: DeliveryEligibility,
  fromDate: Date = new Date(),
): string {
  let daysToAdd = 0

  switch (eligibility) {
    case 'NDD':
      daysToAdd = 1
      break
    case '2DD':
    case '2DD_NO_EMERGENCY':
      daysToAdd = 2
      break
    case '3DD+':
      return 'unknown'
  }

  const deliveryDate = addDays(fromDate, daysToAdd)
  return getDayOfWeek(deliveryDate)
}

/**
 * Format countdown values into readable string
 *
 * @param hours - Number of hours
 * @param minutes - Number of minutes
 * @returns Formatted string (e.g., "2 hrs 30 min")
 */
export function formatCountdown(hours: number, minutes: number): string {
  return `${hours} hrs ${minutes} min`
}

/**
 * Add days to a date
 *
 * @param date - The starting date
 * @param days - Number of days to add
 * @returns New date with days added
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

/**
 * Get the day of week name from a date
 *
 * @param date - The date
 * @returns Day name (e.g., "Monday", "Tuesday")
 */
export function getDayOfWeek(date: Date): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  return days[date.getDay()]
}

/**
 * Check if the time is in the morning (00:00-11:59)
 *
 * @param date - The date to check
 * @returns True if morning
 * // add config for cutoff time..
 */
export function isMorning(date: Date): boolean {
  return date.getHours() < 12
}

/**
 * Check if the time is in the afternoon (12:00-23:59)
 *
 * @param date - The date to check
 * @returns True if afternoon
 */
export function isAfternoon(date: Date): boolean {
  return date.getHours() >= 12
}

/**
 * Calculate countdown timer for NDD and 2DD eligibility
 * Before 12 PM: Countdown to 12:00 PM (noon) cutoff
 * After 12 PM: Countdown to midnight (00:00) cutoff
 * Others: No countdown (returns null)
 *
 * @param eligibility - The delivery eligibility type
 * @param now - Optional date to base calculation on (defaults to current time)
 * @returns Countdown data or null if no countdown applicable
 */
export function calculateCountdown(
  eligibility: DeliveryEligibility,
  now: Date = new Date(),
): CountdownData | null {
  if (eligibility === 'NDD' || eligibility === '2DD') {
    let cutoff: Date

    if (now.getHours() < 12) {
      // Before 12 PM: limit is 12 PM same day
      cutoff = new Date(now)
      cutoff.setHours(12, 0, 0, 0)
    } else {
      // After 12 PM: limit is midnight next day
      cutoff = new Date(now)
      cutoff.setDate(cutoff.getDate() + 1)
      cutoff.setHours(0, 0, 0, 0)
    }

    const diffMs = cutoff.getTime() - now.getTime()

    if (diffMs <= 0) {
      return null
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60))
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

    return {
      hours,
      minutes,
      formattedTime: formatCountdown(hours, minutes),
      totalMinutes: hours * 60 + minutes,
    }
  }

  // No countdown for 2DD_NO_EMERGENCY or 3DD+
  return null
}
